import type { Position } from '../lint/diagnostic';

/**
 * offset ⇄ {line, column}, built once per analysis, queried by binary search.
 *
 * Every check emits a *draft* carrying only a `Span`; the lint engine is the single place
 * that turns offsets into 1-based `Position`s, and it does so through this object
 * (ADR-0008). No check can get line arithmetic wrong because no check does line arithmetic.
 *
 * Offsets are UTF-16 code units — the same unit CodeMirror, `String.prototype.slice` and
 * `jsonc-parser` use. A surrogate pair therefore counts as two columns.
 *
 * This type lives inside `AnalyzedDocument` and never crosses `postMessage`, so it is
 * allowed to have methods (ADR-0010).
 */
export interface LineIndex {
  /** Number of lines. A document without a trailing newline still ends on its last line. */
  readonly lineCount: number;

  /** Length of the indexed source, in UTF-16 code units. */
  readonly length: number;

  /** 1-based {line, column} for `offset`. Offsets outside the document are clamped. */
  positionAt(offset: number): Position;

  /** Inverse of {@link positionAt}. Out-of-range lines and columns are clamped. */
  offsetAt(position: Position): number;

  /** Offset of the first code unit of `line` (1-based). Clamped. */
  lineStartOffset(line: number): number;

  /** Offset just past the last code unit of `line`, not counting its line terminator. */
  lineEndOffset(line: number): number;
}

const CR = 13;
const LF = 10;

/** Offsets at which each line starts. `lineStarts[0]` is always 0. */
function scanLineStarts(source: string): number[] {
  const starts: number[] = [0];

  for (let i = 0; i < source.length; i += 1) {
    const unit = source.charCodeAt(i);

    if (unit === CR) {
      // Treat CRLF as one terminator; a bare CR is a terminator of its own (old Mac files
      // and hand-mangled fixtures both turn up in pasted JSON).
      if (i + 1 < source.length && source.charCodeAt(i + 1) === LF) i += 1;
      starts.push(i + 1);
    } else if (unit === LF) {
      starts.push(i + 1);
    }
  }

  return starts;
}

/** Greatest `i` with `starts[i] <= offset`. `starts` is non-empty and strictly increasing. */
function lineOfOffset(starts: readonly number[], offset: number): number {
  let low = 0;
  let high = starts.length - 1;

  while (low < high) {
    const mid = (low + high + 1) >>> 1;
    if ((starts[mid] ?? 0) <= offset) low = mid;
    else high = mid - 1;
  }

  return low;
}

const clamp = (value: number, low: number, high: number): number =>
  value < low ? low : value > high ? high : value;

export function createLineIndex(source: string): LineIndex {
  const starts = scanLineStarts(source);
  const length = source.length;
  const lineCount = starts.length;

  const startOf = (line: number): number => starts[clamp(line, 1, lineCount) - 1] ?? 0;

  const endOf = (line: number): number => {
    const index = clamp(line, 1, lineCount);
    if (index === lineCount) return length;

    // Back up over the terminator that begins the next line.
    let end = (starts[index] ?? length) - 1;
    if (end > 0 && source.charCodeAt(end) === LF && source.charCodeAt(end - 1) === CR) end -= 1;
    return end < startOf(index) ? startOf(index) : end;
  };

  return {
    lineCount,
    length,

    positionAt(offset: number): Position {
      const at = clamp(Math.trunc(offset), 0, length);
      const line = lineOfOffset(starts, at);
      return { line: line + 1, column: at - (starts[line] ?? 0) + 1 };
    },

    offsetAt(position: Position): number {
      const line = clamp(Math.trunc(position.line), 1, lineCount);
      const start = startOf(line);
      const limit = line === lineCount ? length : (starts[line] ?? length);
      const column = Math.max(1, Math.trunc(position.column));
      return clamp(start + column - 1, start, limit);
    },

    lineStartOffset: startOf,
    lineEndOffset: endOf,
  };
}
